import { useEffect } from "react";
import { useAuth } from "@/lib/auth";

/**
 * AppearanceApplier — applies workspace-wide appearance CSS variables + data
 * attributes to <html>. Includes:
 *   - Base: font-scale, --primary, density
 *   - Advanced: heading/body fonts, heading scale, border radius, shadow style,
 *     background style, per-component surface tints, button style, table style
 *
 * All variables are optional; unset ones fall back to the theme defaults.
 */

const HSL_RE = /^\d{1,3}\s+\d{1,3}%\s+\d{1,3}%$/;
const FONT_GOOGLE_LOAD = new Set();

function _loadFont(family) {
  if (!family || FONT_GOOGLE_LOAD.has(family)) return;
  FONT_GOOGLE_LOAD.add(family);
  const id = `gf-${family.replace(/\s+/g, "-").toLowerCase()}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@300;400;500;600;700;800&display=swap`;
  document.head.appendChild(link);
}

function _setHsl(root, name, value) {
  if (value && HSL_RE.test(value)) root.style.setProperty(name, value);
  else root.style.removeProperty(name);
}

export default function AppearanceApplier() {
  const { activeWorkspace } = useAuth();
  const workspace = activeWorkspace;
  useEffect(() => {
    const root = document.documentElement;
    const a = workspace?.appearance || {};

    // Base
    root.style.setProperty("--font-scale", String(a.font_scale || 1));
    _setHsl(root, "--primary", a.accent_hsl);
    _setHsl(root, "--ring", a.accent_hsl);
    if (a.density_default) root.dataset.density = a.density_default;

    // Advanced — fonts
    if (a.heading_font) {
      _loadFont(a.heading_font);
      root.style.setProperty("--heading-font", `"${a.heading_font}"`);
    } else {
      root.style.removeProperty("--heading-font");
    }
    if (a.body_font) {
      _loadFont(a.body_font);
      root.style.setProperty("--body-font", `"${a.body_font}"`);
    } else {
      root.style.removeProperty("--body-font");
    }
    if (a.heading_scale) root.style.setProperty("--heading-scale", String(a.heading_scale));
    else root.style.removeProperty("--heading-scale");

    // Radius + shadow + background style
    if (typeof a.border_radius === "number") root.style.setProperty("--radius", `${a.border_radius}px`);
    else root.style.removeProperty("--radius");

    root.dataset.shadowStyle = a.shadow_style || "";
    root.dataset.backgroundStyle = a.background_style || "";
    root.dataset.buttonStyle = a.button_style || "";
    root.dataset.tableStyle = a.table_style || "";

    // Per-component HSL tints
    _setHsl(root, "--surface", a.surface_hsl);
    _setHsl(root, "--header-bg", a.header_hsl);
    _setHsl(root, "--sidebar-bg", a.sidebar_hsl);
    _setHsl(root, "--card-bg", a.card_hsl);
    _setHsl(root, "--footer-bg", a.footer_hsl);

    return () => {
      // Reset on unmount / workspace switch
      root.style.removeProperty("--font-scale");
      root.style.removeProperty("--primary");
      root.style.removeProperty("--ring");
      root.style.removeProperty("--heading-font");
      root.style.removeProperty("--body-font");
      root.style.removeProperty("--heading-scale");
      root.style.removeProperty("--radius");
      root.style.removeProperty("--surface");
      root.style.removeProperty("--header-bg");
      root.style.removeProperty("--sidebar-bg");
      root.style.removeProperty("--card-bg");
      root.style.removeProperty("--footer-bg");
      root.dataset.shadowStyle = "";
      root.dataset.backgroundStyle = "";
      root.dataset.buttonStyle = "";
      root.dataset.tableStyle = "";
    };
  }, [workspace?.appearance]);
  return null;
}
